import { notification } from 'antd';
import { observer } from 'mobx-react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import {
  AppStore,
  CarStore,
  CurrentUserStore,
  ShopStore,
  WorkOrderStore,
} from 'stores';
import { EventBus } from 'stores/EventBus';
import styled from 'styled-components';
import { calcMileage } from 'helpers/unitCalculations';
import 'helpers/HelperFunctions';

import { DownOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Divider,
  Dropdown,
  Input,
  Menu,
  Modal,
  Row,
  Spin,
  Typography,
} from 'antd';
import VehicleDefectsTable from 'components/DVIR/VehicleDefectsTable';
import CreateTMTWorkOrderModal from 'containers/WorkOrder/modals/CreateTMTWorkOrderModal';
import CreateWorkOrderModal from 'containers/WorkOrder/modals/CreateWorkOrderModal';
import { get } from 'lodash';
import { customFleets } from 'shared';
import { Logger } from 'stores/Classes';
import { v4 as uuidv4 } from 'uuid';

const DarkHeaderModal = styled(Modal)`
  .ant-modal-content {
    .ant-modal-close {
      .ant-modal-close-x {
        color: #05103a;
      }
    }

    .ant-modal-header {
      .ant-modal-title {
        color: #05103a;
      }
    }

    .ant-modal-body {
      padding: 12px 18px;
    }
  }
`;

const TextArea = styled(Input.TextArea)`
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  padding: 4px 11px;
  min-height: 64px;
  transition: all 0.3s;
  line-height: 1.5715;
  position: relative;
  word-wrap: break-word;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  .tag {
    margin-left: 0;
    font-size: 1.3rem;
    padding: 5px 15px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;

    h1 {
      font-size: 1.5rem;
      margin-bottom: 0.5rem;
    }

    .tag {
      font-size: 1.1rem;
      text-align: center;
    }
  }
`;

const VehicleDefectsRow = styled(Row)`
  .card {
    border-radius: 20px;
  }

  .image {
    border-radius: 20px 20px 0 0;
  }
`;

const GeneralDataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 1rem;

  .grid-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    min-width: 0;

    & > span:first-child {
      flex-shrink: 0;
      margin-right: 0.5rem;
      font-weight: 600;
    }

    & > span:last-child {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  /* For container width less than 800px */
  @container (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 0.75rem;

    .grid-item {
      flex-direction: column;
      align-items: flex-start;
      padding: 0.5rem 0;

      & > span:first-child {
        margin-right: 0;
        margin-bottom: 0.25rem;
        font-size: 0.875rem;
      }

      & > span:last-child {
        margin-left: 0 !important;
        font-size: 0.875rem;
        white-space: normal;
        word-break: break-word;
      }
    }
  }

  /* For very small container width */
  @container (max-width: 500px) {
    grid-template-columns: 1fr;

    .grid-item {
      border-bottom: 1px solid #f0f0f0;
      padding: 0.75rem 0;

      &:last-child {
        border-bottom: none;
      }
    }
  }
`;

const SecondRowGeneralDataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 1rem;
  margin-top: 1rem;

  .grid-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    min-width: 0;

    & > span:first-child,
    & > div:first-child {
      flex-shrink: 0;
      margin-right: 0.5rem;
      font-weight: 600;
    }

    & > span:last-child,
    & > div:last-child {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  /* For container width less than 800px */
  @container (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 0.75rem;
    margin-top: 0.5rem;

    .grid-item {
      flex-direction: column;
      align-items: flex-start;
      padding: 0.5rem 0;

      & > span:first-child,
      & > div:first-child {
        margin-right: 0;
        margin-bottom: 0.25rem;
        font-size: 0.875rem;
      }

      & > span:last-child,
      & > div:last-child {
        margin-left: 0 !important;
        font-size: 0.875rem;
        white-space: normal;
        word-break: break-word;

        /* Special handling for inline-block elements */
        &[style*='inline-block'] {
          display: block !important;
          margin-top: 0.25rem;
        }
      }
    }
  }

  /* For very small container width */
  @container (max-width: 500px) {
    grid-template-columns: 1fr;

    .grid-item {
      border-bottom: 1px solid #f0f0f0;
      padding: 0.75rem 0;

      &:last-child {
        border-bottom: none;
      }
    }
  }
`;

const SignatureRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  .driver-signature {
    border-top: 1px solid black;
    width: 100%;
    text-align: center;
    margin-top: 0.5rem;
  }
`;

const PitstopDropdown = styled(Dropdown)``;

const ButtonWrapper = styled.div`
  button.ant-btn {
    color: #333;
    font-weight: bold;
    font-size: 1.2rem;
    padding: 0.2rem 1.3rem;
    border-radius: 0.3rem;
    display: flex;
    width: 160px;
    align-items: center;
    justify-content: center;
    margin-left: 0;
    border: 1px solid #d9d9d9;
    background-color: white;

    &.green {
      border-color: green;
      color: green;
    }

    &.red {
      border-color: red;
      color: red;
    }

    &.ocean {
      border-color: #003a8c;
      color: #003a8c;
    }

    &.disabled {
      cursor: not-allowed;
      background-color: #f5f5f5;
    }
  }

  @media (max-width: 768px) {
    width: 100%;

    button.ant-btn {
      width: 100%;
      font-size: 1rem;
      padding: 0.4rem 1rem;
    }
  }
`;

const ConfirmationRow = styled(Row)``;

const PageWrapper = styled.div`
  padding: 1rem;
  width: 100%;
  container-type: inline-size;

  @media (max-width: 768px) {
    padding: 0.5rem;

    h1 {
      font-size: 1.25rem;
      line-height: 1.4;
      word-break: break-word;
    }

    h3 {
      font-size: 1.1rem;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
  gap: 1rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
  gap: 1rem;

  .error-message {
    font-size: 1.2rem;
  }
`;

const { Title, Text } = Typography;

class DVIRDetailsPage extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        carId: PropTypes.string.isRequired,
      }),
    }),
  };

  state = {
    isResolvingDVIR: false,
    dvirData: {},
    loading: false,
    error: null,
    samsaraUserSelectedEmail: null,
    mechanicNotes: '',
    resolveDefectModalVisible: false,
    samsaraUsers: [],
    resolveSelectedDefectsModalVisible: false,
    isResolvingDefects: false,
    isCreatingWorkOrder: false,
    isCreatingTMTWorkOrder: false,
    mechanicNotesResolvingMultipleDefects: '',
    defectsToBeResolved: [],
    defectsToCreateWorkOrder: [],
    fetchError: null, // To Check If It's Samsara Integration
    tmtCustomerUser: null,
    tmtRepairShop: null,
    tmtRepairShopValue: null,
    isLoadingSamsaraUsers: false,
    resolveAllDefectsError: false,
    carObject: null,
    carLoading: false,
  };

  componentDidMount() {
    AppStore.setSelectedSidebarKey('/vehicles');
    this.init();
  }

  componentDidUpdate(prevProps) {
    // Check if carId has changed
    const prevCarId = prevProps?.match?.params?.carId;
    const currentCarId = this.props?.match?.params?.carId;

    // Check if trailer param has changed
    const prevQueryParams = prevProps.history?.location?.search;
    const currentQueryParams = this.props.history?.location?.search;

    let prevIsTrailer = false;
    let currentIsTrailer = false;

    if (prevQueryParams) {
      const urlParams = new URLSearchParams(prevQueryParams);
      prevIsTrailer = urlParams.get('trailer') === 'true';
    }

    if (currentQueryParams) {
      const urlParams = new URLSearchParams(currentQueryParams);
      currentIsTrailer = urlParams.get('trailer') === 'true';
    }

    // Reload if carId or trailer status has changed
    if (prevCarId !== currentCarId || prevIsTrailer !== currentIsTrailer) {
      this.init();
    }
  }

  vehicleDefectsTableRef = null;
  trailerDefectsTableRef = null;

  isGeotabFleet(dvirId = null) {
    const id = dvirId || this.state.dvirData?.dvirId;
    return id && isNaN(id);
  }

  init = async () => {
    try {
      // Reset state to initial values
      this.setState({
        loading: true,
        error: null,
        isLoadingSamsaraUsers: true,
        isResolvingDVIR: false,
        dvirData: {},
        samsaraUserSelectedEmail: null,
        mechanicNotes: '',
        resolveDefectModalVisible: false,
        samsaraUsers: [],
        resolveSelectedDefectsModalVisible: false,
        isResolvingDefects: false,
        isCreatingWorkOrder: false,
        isCreatingTMTWorkOrder: false,
        mechanicNotesResolvingMultipleDefects: '',
        defectsToBeResolved: [],
        defectsToCreateWorkOrder: [],
        fetchError: null,
        tmtCustomerUser: null,
        tmtRepairShop: null,
        tmtRepairShopValue: null,
        carObject: null,
        carLoading: true,
      });
      const carId = this.props?.match?.params?.carId;
      const queryParams = this.props.history?.location?.search;
      let isTrailer = false;
      if (queryParams) {
        const urlParams = new URLSearchParams(queryParams);
        isTrailer = urlParams.get('trailer');
      }

      // Check if isTrailer is set via URL parameter or props
      if (isTrailer === 'true' || this.props.isTrailer) {
        isTrailer = 'trailer';
      }

      // Fetch the car object asynchronously as we're not using it for the DVIR data
      CarStore.getCarById(carId)
        .then(() => {
          const carObject = CarStore.data.get(Number(carId));
          this.setState({ carObject, carLoading: false });
        })
        .catch((error) => {
          Logger.log('Error fetching car data');
          Logger.error(error);
          this.setState({ carLoading: false });
        });

      const dvirData = await CarStore.fetchDVIRsForCars(carId, isTrailer);

      if (dvirData.length) {
        const { record } = dvirData[0];
        this.setState({
          dvirData: this.mergeDefectPhotos(record),
        });

        const defects =
          record.payload?.vehicleDefects ||
          record.payload?.trailerDefects ||
          [];

        // Add to defect, information about Work Orders
        if (defects?.length > 0) {
          const workOrderDvirData = await WorkOrderStore.searchWorkOrdersDvir({
            dvirId: record.dvirId,
          });
          if (workOrderDvirData.length) {
            defects.map((defect) => {
              const filteredData = workOrderDvirData.find(
                (woDvir) =>
                  woDvir?.defect_id?.toString() === defect?.id?.toString()
              );
              defect.workOrder = filteredData;
              return defect;
            });
          }
        }

        this.setState({
          dvirData: this.mergeDefectPhotos(record),
        });
      }

      this.setState({ loading: false });
    } catch (error) {
      Logger.log('Error fetching DVIR data', error);
      Logger.error(error);
      this.setState({
        error: 'Something went wrong, couldn\'t fetch DVIR',
        loading: false,
      });
      return; // Exit early if DVIR data fetch fails
    }

    // Skip Samsara users fetch for Geotab fleets
    if (!this.isGeotabFleet()) {
      try {
        const samsaraUsers = await CarStore.fetchSamsaraUsers(
          ShopStore.currentShop.id
        );
        // Select the current user email if it's in the list
        const currentUserEmail = CurrentUserStore.user.email;
        const currentUser = samsaraUsers.find(
          (user) =>
            String(user.email).toLowerCase() ===
            String(currentUserEmail).toLowerCase()
        );
        if (currentUser) {
          // loop through the list of defects and set the current user as the default
          const dvirData = this.state.dvirData;
          if (
            dvirData?.payload?.vehicleDefects &&
            dvirData?.payload?.vehicleDefects.length
          ) {
            dvirData.payload.vehicleDefects.forEach((defect) => {
              if (!defect.resolvedBySamsaraUserId) {
                defect.shouldDisableSelect = true;
                defect.resolvedBySamsaraUserId = currentUser.id;
              }
            });
          }
          if (
            dvirData?.payload?.trailerDefects &&
            dvirData?.payload?.trailerDefects.length
          ) {
            dvirData.payload.trailerDefects.forEach((defect) => {
              if (!defect.resolvedBySamsaraUserId) {
                defect.shouldDisableSelect = true;
                defect.resolvedBySamsaraUserId = currentUser.id;
              }
            });
          }
        }
        this.setState({ samsaraUsers: samsaraUsers || [] });
      } catch (error) {
        // extract the error message from the error object
        Logger.error(error);
        this.setState({ fetchError: error });
        // AppStore.addError('Error fetching Samsara users');
      }
    }

    // Skip TMT work order users fetch for Geotab fleets
    if (!this.isGeotabFleet()) {
      try {
        const workOrderSamsaraUsers =
          await WorkOrderStore.getWorkOrderSamsaraUsersByShopId(
            ShopStore.currentShop.id
          );
        const currentUserEmail = CurrentUserStore.user.email;
        const currentUser = workOrderSamsaraUsers.find(
          (user) =>
            String(user.email).toLowerCase() ===
            String(currentUserEmail).toLowerCase()
        );
        if (currentUser) {
          const customerTMTUser = currentUser.customer_user;
          const repairShop = currentUser.repair_shop;

          if (customerTMTUser) {
            this.setState({ tmtCustomerUser: customerTMTUser });
            if (repairShop && typeof repairShop === 'object') {
              const [tmtRepairShop, tmtRepairShopValue] =
                Object.entries(repairShop)[0]; // Get the first key-value pair
              this.setState({
                tmtRepairShop: `${tmtRepairShop}, ${tmtRepairShopValue}`,
              });
              this.setState({ tmtRepairShopValue });
            } else {
              console.warn(
                'Invalid repair_shop format or no repair shop available.'
              );
            }
          }
        }
      } catch (error) {
        // extract the error message from the error object
        Logger.error(error);
        this.setState({ fetchError: error });
        // AppStore.addError('Error fetching Samsara users');
      }
    }

    this.setState({ isLoadingSamsaraUsers: false });
  };
  mergeDefectPhotos(data) {
    if (
      !data ||
      !data.payload ||
      (!data.payload.vehicleDefects && !data.payload.trailerDefects) ||
      !data.defect_details
    ) {
      return data;
    }

    const defectDetailsMap = {};
    data.defect_details.forEach((detail) => {
      defectDetailsMap[detail.defect_id] = detail.defect_photos || [];
    });

    const mergePhotos = (defects) => {
      if (!defects) return;
      defects.forEach((defect) => {
        const defectPhotos = defectDetailsMap[defect.id];
        if (defectPhotos) {
          defect.defectPhotos = defectPhotos;
        }
      });
    };

    mergePhotos(data.payload.vehicleDefects);
    mergePhotos(data.payload.trailerDefects);

    delete data.defect_details;

    return data;
  }

  formatSafetyStatus = (status) => {
    switch (status) {
      case 'safe':
        return 'SAFE';
      case 'unsafe':
        return 'UNSAFE';
      case 'resolved':
        return 'RESOLVED';
      default:
        return 'N/A';
    }
  };

  getStatusColor = (status) => {
    switch (status) {
      case 'safe':
        return 'green';
      case 'unsafe':
        return 'red';
      case 'resolved':
        return 'geekblue';
      default:
        return 'grey';
    }
  };

  formatType = (type) => {
    switch (type) {
      case 'preTrip':
        return 'Pre-Trip';
      case 'postTrip':
        return 'Post-Trip';
      case 'InTrip':
        return 'In-Trip';
      default:
        return 'N/A';
    }
  };

  handleResolveDVIR = async () => {
    try {
      this.setState({ isResolvingDVIR: true, resolveAllDefectsError: false });
      if (!CurrentUserStore.user.email) {
        throw new Error('An user is required to resolve the DVIR');
      }

      // For Geotab fleets, resolving all defects is mandatory
      if (this.isGeotabFleet()) {
        const resolveAllDefectsChecked = document.getElementById(
          'resolveAllDefectsCheckbox'
        ).checked;
        if (!resolveAllDefectsChecked) {
          this.setState({
            isResolvingDVIR: false,
            resolveAllDefectsError: true,
          });
          return;
        }
      }

      if (this.isGeotabFleet()) {
        // For Geotab fleets, just resolve all defects - the API will handle DVIR status
        const allDefects = [
          ...get(this, 'state.dvirData.payload.vehicleDefects', []),
          ...get(this, 'state.dvirData.payload.trailerDefects', []),
        ].filter((defect) => !defect.resolvedBy);

        if (allDefects.length > 0) {
          const defectIds = allDefects.map((defect) => defect.id);
          await CarStore.resolveGeotabDVIRDefects({
            shopId: ShopStore.currentShop.id,
            dvirId: this.state.dvirData.dvirId,
            defectIds: defectIds,
            mechanicNotes: this.state.mechanicNotes || 'Resolved by Pitstop',
            resolvedBy: CurrentUserStore.user.email,
          });
          AppStore.addNotification(
            `DVIR and ${defectIds.length} defects resolved successfully`
          );
        } else {
          AppStore.addNotification('DVIR resolved successfully');
        }
      } else {
        // Handle Samsara fleet
        await CarStore.resolveDVIR({
          recordId: `${this.state.dvirData.id}`, // requires type string
          samsaraUserEmail: CurrentUserStore.user.email,
          mechanicNotes: this.state.mechanicNotes,
          shopId: ShopStore.currentShop.id,
        });
        // AppStore.addNotification('DVIR resolved successfully');

        // check if the resolve all defects checkbox is checked
        if (document.getElementById('resolveAllDefectsCheckbox').checked) {
          // resolve all defects
          const currentUserEmail = CurrentUserStore.user.email;
          const samsaraUserResolving = this.state.samsaraUsers.find(
            (user) =>
              String(user.email).toLowerCase() ===
              String(currentUserEmail).toLowerCase()
          );
          const defectsResolved = await CarStore.resolveDVIRDefects({
            shopId: ShopStore.currentShop.id,
            dvirId: this.state.dvirData.id,
            defects: get(this, 'state.dvirData.payload.vehicleDefects', []),
            mechanicNotes: this.state.mechanicNotes || '',
            resolvedBySamsaraUserId:
              samsaraUserResolving?.id || currentUserEmail,
          });
          AppStore.addNotification(
            `${defectsResolved.length} defects resolved successfully`
          );
        }
      }

      this.setState({
        samsaraUserEmail: '',
        mechanicNotes: '',
        resolveDefectModalVisible: false,
      });
      this.init();
    } catch (error) {
      AppStore.addError('Error resolving DVIR');
    } finally {
      this.setState({ isResolvingDVIR: false });
    }
  };

  handleResolveDVIRCancel = () => {
    this.setState(
      {
        mechanicNotes: '',
        resolveDefectModalVisible: false,
        resolveAllDefectsError: false,
      },
      () => {
        // reset the resolve all defects checkbox
        document.getElementById('resolveAllDefectsCheckbox').checked = false;
      }
    );
  };

  handleDVIRResolveClick = async () => {
    this.setState({ resolveDefectModalVisible: true }, () => {
      // focus on the input using react ref
    });
  };

  handleDefectMechanicNotesChange = (defect, value, type = 'vehicles') => {
    const toUpdate = {};

    if (type === 'trailers') {
      toUpdate.trailerDefects =
        this.state.dvirData?.payload?.trailerDefects.map((d) => {
          if (d.id === defect.id) {
            return {
              ...d,
              mechanicNotes: value,
            };
          }
          return d;
        });
    } else {
      toUpdate.vehicleDefects =
        this.state.dvirData?.payload?.vehicleDefects.map((d) => {
          if (d.id === defect.id) {
            return {
              ...d,
              mechanicNotes: value,
            };
          }
          return d;
        });
    }

    this.setState({
      dvirData: {
        ...this.state.dvirData,
        payload: {
          ...this.state.dvirData?.payload,
          ...toUpdate,
        },
      },
    });
  };

  handleDefectResolvedByChange = (defect, userId) => {
    this.setState({
      dvirData: {
        ...this.state.dvirData,
        payload: {
          ...this.state.dvirData?.payload,
          vehicleDefects: this.state.dvirData?.payload?.vehicleDefects.map(
            (d) => {
              if (d.id === defect.id) {
                return {
                  ...d,
                  resolvedBySamsaraUserId: userId,
                };
              }
              return d;
            }
          ),
        },
      },
    });
  };

  handleToggleMechanicNoteOnDefectClick = (defect, isVisible) => {
    this.setState({
      dvirData: {
        ...this.state.dvirData,
        payload: {
          ...this.state.dvirData?.payload,
          vehicleDefects: this.state.dvirData?.payload?.vehicleDefects.map(
            (d) => {
              if (d.id === defect.id) {
                return {
                  ...d,
                  mechanicNotesVisible: isVisible,
                };
              }
              return d;
            }
          ),
        },
      },
    });
  };

  handleMechanicNotesChangeSave = async (
    defect,
    mechanicNotes,
    type = 'vehicles'
  ) => {
    try {
      AppStore.addLoading(
        `Saving ${this.isGeotabFleet() ? 'latest remark' : 'mechanic notes'}...`
      );
      if (this.isGeotabFleet()) {
        // Handle Geotab fleet
        await CarStore.saveGeotabDVIRLatestRemark({
          shopId: ShopStore.currentShop.id,
          dvirId: this.state.dvirData.id,
          defectId: defect.id,
          latestRemark: mechanicNotes,
          updatedBy: CurrentUserStore.user.email,
        });
        //Get latest comments from the API
        this.init();
      } else {
        // Handle Samsara fleet
        await CarStore.saveMechanicNotes({
          dvirId: this.state.dvirData.id,
          defectId: defect.id,
          mechanicNotes: mechanicNotes,
          shopId: ShopStore.currentShop.id,
        });
      }

      AppStore.addNotification(
        `${this.isGeotabFleet() ? 'Latest remark' : 'Mechanic notes'} saved successfully`
      );
      if (type === 'trailers') {
        this.trailerDefectsTableRef.setEditingMechanicNotes(
          this.trailerDefectsTableRef.editingMechanicNotes.filter(
            (id) => id !== defect.id
          )
        );
      } else {
        this.vehicleDefectsTableRef.setEditingMechanicNotes(
          this.vehicleDefectsTableRef.editingMechanicNotes.filter(
            (id) => id !== defect.id
          )
        );
      }
    } catch (error) {
      let errMessage = 'Error saving notes';
      if (error?.message) {
        errMessage = error.message;
      }

      if (this.isGeotabFleet()) {
        // Geotab-specific error handling
        errMessage =
          'Error saving latest remark for Geotab DVIR. Please try again later.';
        // if (error?.message) {
        //   errMessage = `Geotab API Error: ${error.message}`;
        // }
      } else {
        // Samsara-specific error handling
        if (errMessage.includes('Author Id is not valid')) {
          const email = CurrentUserStore.user.email;
          errMessage = `To resolve DVIR and defects, please review your Samsara account to make sure your Pitstop e-mail (${email}) matches with your Samsara email. If you want to change your Pitstop email, please contact us.`;
        }
        if (errMessage.includes('failed to call samsara API:')) {
          errMessage = errMessage.replace('failed to call samsara API:', '');
        }
      }

      AppStore.addError(errMessage, {
        duration: 0,
      });
    } finally {
      AppStore.removeLoading();
    }
  };

  handleDefectResolveConfirm = async (defect, type = 'vehicles') => {
    try {
      AppStore.addLoading('Resolving defect...');

      if (this.isGeotabFleet()) {
        // Handle Geotab fleet
        await CarStore.resolveGeotabDVIRDefects({
          shopId: ShopStore.currentShop.id,
          dvirId: this.state.dvirData.dvirId,
          defectIds: [defect.id],
          mechanicNotes: defect.mechanicNotes || '',
          resolvedBy: CurrentUserStore.user.email,
        });
      } else {
        // Handle Samsara fleet
        await CarStore.resolveDVIRDefect({
          dvirId: this.state.dvirData.id,
          defectId: defect.id,
          mechanicNotes: defect.mechanicNotes || '',
          isResolved: true,
          resolvedBySamsaraUserId:
            defect.resolvedBySamsaraUserId || CurrentUserStore.user.email,
          shopId: ShopStore.currentShop.id,
        });
      }

      AppStore.addNotification('Defect resolved successfully');
      this.init();
    } catch (error) {
      let errMessage = 'Error resolving defect';
      if (error?.message) {
        errMessage = error.message;
      }
      if (errMessage.includes('Author Id is not valid')) {
        const email = CurrentUserStore.user.email;
        errMessage = `To resolve DVIR and defects, please review your Samsara account to make sure your Pitstop e-mail (${email}) matches with your Samsara email. If you want to change your Pitstop email, please contact us.`;
      }
      if (errMessage.includes('failed to call samsara API:')) {
        // remove 'failed to call samsara API' from the error message
        errMessage = errMessage.replace('failed to call samsara API:', '');
      }
      AppStore.addError(errMessage, {
        duration: 0,
      });
    } finally {
      AppStore.removeLoading();
    }
  };

  handleMarkAllAsResolvedClick = async (defectsToBeResolved = []) => {
    if (defectsToBeResolved.length === 0) {
      AppStore.addError('No defects to resolve');
      return;
    }
    // show modal to confirm
    this.setState({
      resolveSelectedDefectsModalVisible: true,
      defectsToBeResolved,
    });
  };

  handleResolveSelectedDefectsCancel = () => {
    this.setState({ resolveSelectedDefectsModalVisible: false });
  };

  handleResolveSelectedDefects = async () => {
    try {
      this.setState({ isResolvingDefects: true });
      if (!CurrentUserStore.user.email) {
        throw new Error('An user is required to resolve the DVIR');
      }

      if (this.isGeotabFleet()) {
        // Handle Geotab fleet
        const defectIds = this.state.defectsToBeResolved.map(
          (defect) => defect.id
        );
        await CarStore.resolveGeotabDVIRDefects({
          shopId: ShopStore.currentShop.id,
          dvirId: this.state.dvirData.dvirId,
          defectIds: defectIds,
          mechanicNotes: this.state.mechanicNotesResolvingMultipleDefects || '',
          resolvedBy: CurrentUserStore.user.email,
        });
        AppStore.addNotification(
          `${defectIds.length} defects resolved successfully`
        );
      } else {
        // Handle Samsara fleet
        const currentUserEmail = CurrentUserStore.user.email;
        const samsaraUserResolving = this.state.samsaraUsers.find(
          (user) =>
            String(user.email).toLowerCase() ===
            String(currentUserEmail).toLowerCase()
        );
        const defectsResolved = await CarStore.resolveDVIRDefects({
          shopId: ShopStore.currentShop.id,
          dvirId: this.state.dvirData.id,
          defects: this.state.defectsToBeResolved,
          mechanicNotes: this.state.mechanicNotesResolvingMultipleDefects || '',
          resolvedBySamsaraUserId: samsaraUserResolving?.id || currentUserEmail,
        });
        AppStore.addNotification(
          `${defectsResolved.length} defects resolved successfully`
        );
      }

      this.setState({
        mechanicNotesResolvingMultipleDefects: '',
        resolveSelectedDefectsModalVisible: false,
      });
      this.init();
    } catch (error) {
      console.error(error);
      AppStore.addError(
        'Error resolving the selected defects. Please try again later'
      );
    } finally {
      this.setState({ isResolvingDefects: false });
    }
  };

  handleDefectResolveClick = async (key, defect, type = 'vehicles') => {
    const toUpdate = {};

    if (type === 'trailers') {
      toUpdate.trailerDefects =
        this.state.dvirData?.payload?.trailerDefects.map((d) => {
          if (d.id === defect.id) {
            return {
              ...d,
              isResolved: key === 'resolved',
            };
          }
          return d;
        });
    } else {
      toUpdate.vehicleDefects =
        this.state.dvirData?.payload?.vehicleDefects.map((d) => {
          if (d.id === defect.id) {
            return {
              ...d,
              isResolved: key === 'resolved',
            };
          }
          return d;
        });
    }

    this.setState({
      dvirData: {
        ...this.state.dvirData,
        payload: {
          ...this.state.dvirData?.payload,
          ...toUpdate,
        },
      },
    });
  };

  navegateToNewWorkOrder = (id) => {
    this.props.history.push(`/work-order/${id}/edit`);
  };

  handleCreateWorkOrderClick = async (defectsToCreateWorkOrder = []) => {
    if (defectsToCreateWorkOrder.length === 0) {
      AppStore.addError('No defects to create work order');
      return;
    }

    AppStore.openModals.set('CREATE_WORK_ORDER_MODAL_ID', true);

    // show modal to confirm
    this.setState({
      defectsToCreateWorkOrder,
    });
  };

  handleCreateWorkOrder = async (workOrder) => {
    try {
      this.setState({ isCreatingWorkOrder: true });
      if (!CurrentUserStore.user.email) {
        throw new Error('An user is required to create a work order');
      }

      const newWorkOrder = await this.createWorkOrderFromDefects(workOrder);

      if (!newWorkOrder) {
        return;
      }

      // If successfully created a work order, create the lines
      await this.createWorkOrderLines(newWorkOrder.id);

      notification.success({
        message: 'Work orders saved',
        key: 'orderUpdated',
        description: (
          <div>
            The Work Order was created{' '}
            <Button
              style={{
                padding: 0,
              }}
              type="link"
              onClick={() => this.navegateToNewWorkOrder(newWorkOrder.id)}
            >
              Click Here
            </Button>{' '}
            to view the details and to make edits
          </div>
        ),
      });

      this.setState({
        defectsToCreateWorkOrder: [],
      });

      // Refresh the states
      this.init();

      this.setState({ isCreatingWorkOrder: false });
      AppStore.openModals.set('CREATE_WORK_ORDER_MODAL_ID', false);
      WorkOrderStore.clearShopWorkOrdersCache(ShopStore.currentShop.id);

      // Emit event to refresh vehicles table data
      EventBus.$emit('workOrders:fetchLatestWorkOrdersForCar');
    } catch (error) {
      console.error(error);
      AppStore.addError(
        'Error creating work order for the selected defects. Please try again later'
      );
    } finally {
      this.setState({ isCreatingWorkOrder: false });
      AppStore.openModals.set('CREATE_WORK_ORDER_MODAL_ID', false);
    }
  };

  createWorkOrderFromDefects = async (workOrder) => {
    const workOrderDvir = [];
    this.state.defectsToCreateWorkOrder.forEach((defect) => {
      workOrderDvir.push({
        dvir_id: this.state.dvirData.dvirId,
        defect_id: defect.id,
      });
    });

    const response = await WorkOrderStore.createWorkOrderOnCustomEndpoint({
      workOrder: {
        id_car: +this.props.match.params.carId,
        id_shop: +ShopStore.currentShop.id,
        status: workOrder.woStatus,
        type: workOrder.woRepairType,
        priority: workOrder.woRepairPriority,
        invoice_number: workOrder.woInvoiceNumber,
        created_by: workOrder.woCreatedBy,
        assigned_to: workOrder.woAssignedTo,
        started_at: moment(new Date()).toDate(),
        migrate_source: 'PitStop',
        location_code: workOrder.woLocation,
      },
      workOrderDvir,
    });

    return response;
  };

  createPriority(value) {
    switch (value) {
      case 'major':
        return 5;
      case 'minor':
        return 1;
      case 'critical':
        return 10;
      default:
        return 5;
    }
  }

  createTMTWorkOrderFromDefects = async (workOrder) => {
    try {
      // GET USERS
      // To be implemented

      // Use these values to match PitStop vehicle with TMT vehicle
      if (!this.state.tmtCustomerUser) {
        AppStore.addError(
          'TMT Customer User is required to create a TMT work order.'
        );
        return;
      }

      if (!this.state.tmtRepairShopValue) {
        AppStore.addError(
          'TMT Repair Shop value is required to create a TMT work order.'
        );
        return;
      }

      const unitTMT = await WorkOrderStore.getCarShopTMTInfo({
        pitstopCarId: +this.props.match.params.carId,
      });

      const workOrderSectionsTMTpayload = [];
      const workOrderDvir = [];

      const highestPriority = Math.max(
        workOrder.map((wo) => {
          return wo.repairPriority;
        })
      );

      const userTimezone =
        CurrentUserStore.user?.settings?.timezone || 'America/Toronto';

      workOrder.forEach((section) => {
        workOrderSectionsTMTpayload.push({
          complaint: section.complaint,
          componentCode: section.componentCode,
          modifiedBy: this.state.tmtCustomerUser, // CHange by the real user
          openedDate: moment
            .tz(moment(), userTimezone)
            .format('YYYY-MM-DDTHH:mm:ss'),
          priority: this.createPriority(section.repairPriority),
          repairReason: section.repairReason,
          sectionComment: section.defectType + ' - ' + section.comment,
          sectionNumber: 0,
          sectionStatus: 'OPEN',
          lines: [],
        });

        // Use to Link Work Orders and DVIR (Defects needs to be attached to TMT Sections/ PitStop Order Lines)
        workOrderDvir.push({
          dvir_id: this.state.dvirData.dvirId,
          defect_id: section.defectId,
          defect_type: section.defectType,
        });
      });

      const workOrderTMTpayload = {
        amountUnitOfMeasure: 'US$',
        modifiedBy: this.state.tmtCustomerUser, // CHange by the real user
        openedDate: moment
          .tz(moment(), userTimezone)
          .format('YYYY-MM-DDTHH:mm:ss'),
        priority: this.createPriority(highestPriority),
        unitId: unitTMT[0].unit_id,
        repairClass: 'BEFOREFAIL', // Always BEFOREFAIL = DVIR on Header
        repairShop: this.state.tmtRepairShopValue, // SH -> 201-1 unitTMT[0].repair_shop,
        repairSite: 'FACILITY',
        caller: 'null',
        companyId: 'TMT',
        sections: workOrderSectionsTMTpayload,
      };

      const workOrderPitStopPayload = {
        id_car: +this.props.match.params.carId,
        id_shop: +ShopStore.currentShop.id,
        status: 'open',
        type: 'driver_identified',
        priority: workOrder[0].repairPriority,
        created_by: workOrder[0].createdBy,
        assigned_to: workOrder[0].assignedTo,
        workOrderDvir,
      };

      const response = await WorkOrderStore.createWorkOrderOnTMTCustomEndpoint({
        workOrderTMTpayload,
        workOrderPitStopPayload,
      });

      return response;
    } catch (error) {
      await this.sendErrorLogToAPI(error);
      throw error;
    }
  };

  sendErrorLogToAPI = async (error) => {
    let errorMessage = '';

    try {
      if (error instanceof Error) {
        errorMessage = error.message || 'Unknown error';

        if (error.stack) {
          const stackLines = error.stack.split('\n').slice(0, 10);
          errorMessage += '\nStack: ' + stackLines.join('\n');
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (typeof error === 'object') {
        try {
          const stringified = JSON.stringify(error);
          errorMessage =
            stringified.length > 5000
              ? stringified.substring(0, 5000) + '... (truncated)'
              : stringified;
        } catch (stringifyError) {
          errorMessage = 'Error object could not be stringified';
        }
      } else {
        errorMessage = String(error);
      }

      if (errorMessage.length > 10000) {
        errorMessage = errorMessage.substring(0, 10000) + '... (truncated)';
      }

      await CarStore.createDVIRLogs(errorMessage);
    } catch (logError) {
      console.error('Failed to send error log to API:', logError);
    }
  };

  createWorkOrderLines = async (workOrderID) => {
    const workOrderLines = [];
    this.state.defectsToCreateWorkOrder.forEach((defects) => {
      const key = uuidv4();
      workOrderLines.push({
        description: defects.comment,
        title: defects.defectType,
        defects: [
          {
            key,
            name: `Defect ${key}`,
            dvirId: this.state.dvirData.dvirId,
            comment: defects.comment,
            id_defect: defects.id,
            isResolved: defects.isResolved,
          },
        ],
      });
    });

    const response = await WorkOrderStore.saveWorkOrderLinesOnCustomEndpoint(
      workOrderID,
      { workOrderLines }
    );

    return response;
  };

  handleCreateTMTWorkOrderClick = async (defectsToCreateWorkOrder = []) => {
    if (defectsToCreateWorkOrder.length === 0) {
      AppStore.addError('No defects to create work order');
      return;
    }

    AppStore.openModals.set('CREATE_TMT_WORK_ORDER_MODAL_ID', true);

    // show modal to confirm
    this.setState({
      defectsToCreateWorkOrder,
    });
  };

  handleCreateTMTWorkOrder = async (workOrder) => {
    try {
      console.log('At DVIR level', workOrder);

      this.setState({ isCreatingTMTWorkOrder: true });
      if (!CurrentUserStore.user.email) {
        throw new Error('An user is required to create a work order');
      }

      const newWorkOrder = await this.createTMTWorkOrderFromDefects(workOrder);

      if (!newWorkOrder) {
        return;
      }

      notification.open({
        message: 'WO Created - Assignment Needed',
        duration: 0,
        description: (
          <div>
            <p>
              Your work order has been successfully created in TMT. Don't forget
              to <b>assign it to someone in TMT</b> to ensure it gets completed.
            </p>
            <button
              type="link"
              onClick={() => this.navegateToNewWorkOrder(newWorkOrder.id)}
              style={{
                textDecoration: 'underline',
                color: '#1890ff',
                border: 'none',
                padding: 0,
                backgroundColor: 'transparent',
                cursor: 'pointer',
              }}
            >
              View Pitstop Work Order: #{newWorkOrder.invoice_number}
            </button>
          </div>
        ),
      });

      this.setState({
        defectsToCreateWorkOrder: [],
      });

      // Refresh the states
      this.init();

      this.setState({ isCreatingTMTWorkOrder: false });
      AppStore.openModals.set('CREATE_TMT_WORK_ORDER_MODAL_ID', false);
    } catch (error) {
      console.error(error);
      AppStore.addError(
        'Error creating work order for the selected defects. Please try again later'
      );
    } finally {
      this.setState({ isCreatingTMTWorkOrder: false });
      AppStore.openModals.set('CREATE_TMT_WORK_ORDER_MODAL_ID', false);
    }
  };

  getSafetyStatusColor = () => {
    if (this.state.dvirData?.payload?.safetyStatus === 'safe') {
      return 'green';
    }
    if (this.state.dvirData?.payload?.safetyStatus === 'unsafe') {
      return 'red';
    }
    return 'ocean';
  };

  getSafetyStatusText = () => {
    if (this.state.dvirData?.payload?.safetyStatus === 'safe') {
      return 'SAFE';
    }
    if (this.state.dvirData?.payload?.safetyStatus === 'unsafe') {
      return 'UNSAFE';
    }
    return 'RESOLVED';
  };

  render() {
    if (this.state.loading) {
      return (
        <LoadingContainer>
          <Spin size="large" />
          <Text>Loading DVIR Data</Text>
        </LoadingContainer>
      );
    }

    if (this.state.error) {
      return (
        <ErrorContainer>
          <Text type="danger" className="error-message">
            {this.state.error}
          </Text>
          <Button
            type="primary"
            onClick={() => this.init()}
            loading={this.state.loading}
          >
            Try Again
          </Button>
        </ErrorContainer>
      );
    }
    // const statusColor = this.getStatusColor(this.state.dvirData?.safetyStatus);
    return (
      <Fragment>
        <PageWrapper>
          <TitleWrapper>
            <Title level={2} style={{ marginBottom: 0 }}>
              Driver Vehicle Inspection Reports -{' '}
              {
                // if trailer DVIR, show trailer name
                this.state.dvirData?.payload?.vehicle?.name
                  ? `Vehicle # ${this.state.dvirData?.payload?.vehicle?.name}`
                  : `Trailer # ${this.state.dvirData?.payload?.trailer?.name}`
              }
            </Title>
            {/* <Tag className="tag" color={statusColor}>
            {this.formatSafetyStatus(this.state.dvirData?.safetyStatus)}
          </Tag> */}
            <ButtonWrapper>
              <PitstopDropdown
                className="pitstopDropdown"
                trigger={['click']}
                disabled={
                  this.state.dvirData?.payload?.safetyStatus !== 'unsafe'
                }
                dropdownRender={() => (
                  <Menu onClick={() => this.handleDVIRResolveClick()}>
                    <Menu.Item key="resolved">RESOLVED (SAFE)</Menu.Item>
                  </Menu>
                )}
                placement="bottomCenter"
              >
                <Button
                  className={`${this.getSafetyStatusColor()} ${
                    this.state.dvirData?.payload?.safetyStatus !== 'unsafe'
                      ? 'disabled'
                      : ''
                  }`}
                  disabled={
                    this.state.dvirData?.payload?.safetyStatus === 'safe'
                  }
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    {this.getSafetyStatusText()}
                    {this.state.dvirData?.payload?.safetyStatus !==
                    'unsafe' ? null : (
                      <DownOutlined />
                    )}
                  </div>
                </Button>
              </PitstopDropdown>
            </ButtonWrapper>
          </TitleWrapper>
          {/* Create two rows with 3 columns inside each */}
          <GeneralDataGrid style={{ marginTop: '1rem' }}>
            <div className="grid-item">
              <Text strong>Inspection Type:</Text>
              <Text style={{ marginLeft: '1rem' }}>
                {this.formatType(this.state.dvirData?.payload?.type)}
              </Text>
            </div>
            <div className="grid-item">
              <Text strong>Created By:</Text>
              <Text style={{ marginLeft: '1rem' }}>
                {
                  this.state.dvirData?.payload?.authorSignature?.signatoryUser
                    ?.name
                }
              </Text>
            </div>
            <div className="grid-item">
              <Text strong>
                {
                  // if trailer DVIR, show trailer name
                  this.state.dvirData?.payload?.vehicle?.name
                    ? 'Vehicle'
                    : 'Trailer'
                }{' '}
                ID:
              </Text>
              <Text style={{ marginLeft: '1rem' }}>
                {this.state.dvirData?.payload?.vehicle?.name ||
                  this.state.dvirData?.payload?.trailer?.name}
              </Text>
            </div>
            <div className="grid-item">
              <Text strong>Odometer:</Text>
              {this.state.carLoading ? (
                <Text style={{ marginLeft: '1rem' }}>
                  <Spin size="small" />
                </Text>
              ) : (
                <Text style={{ marginLeft: '1rem' }}>
                  {this.state.carObject?.totalMileage
                    ? (() => {
                        const unit =
                          CurrentUserStore?.user?.displayedOdometer || 'miles';
                        return `${Number(calcMileage(this.state.carObject.totalMileage).toFixed(1)).toReadable()} ${unit === 'miles' ? 'mi' : unit === 'km' ? 'km' : unit ? 'km' : 'mi'}`;
                      })()
                    : 'N/A'}
                </Text>
              )}
            </div>
          </GeneralDataGrid>

          <SecondRowGeneralDataGrid>
            <div className="grid-item">
              <Text strong>Location:</Text>
              <Text style={{ marginLeft: '1rem' }}>
                {this.state.dvirData?.payload?.location || 'N/A'}
              </Text>
            </div>
            <div className="grid-item">
              <Text strong>
                {this.isGeotabFleet ? 'Remark:' : 'Mechanic Notes:'}
              </Text>
              <Text style={{ marginLeft: '1rem' }}>
                {this.state.dvirData?.payload?.mechanicNotes || 'N/A'}
              </Text>
            </div>
            {customFleets.summitMaterialsAll.includes(
              ShopStore.currentShop.id
            ) && (
              <div className="grid-item">
                <Text strong>TMT Customer User:</Text>
                <div style={{ marginLeft: '1rem', display: 'inline-block' }}>
                  {this.state.isLoadingSamsaraUsers ? (
                    <Spin size="small" /> // Display a small loading spinner
                  ) : (
                    <Text>{this.state.tmtCustomerUser || 'N/A'}</Text> // Display the user or "N/A" when loading completes
                  )}
                </div>
              </div>
            )}
          </SecondRowGeneralDataGrid>

          {this.state.dvirData?.payload?.vehicleDefects?.length > 0 && (
            <>
              <Divider />
              <VehicleDefectsRow gutter={16}>
                <Col span={24}>
                  <Title level={3}>Vehicle Defects</Title>
                </Col>
                <Col span={24}>
                  <VehicleDefectsTable
                    data={this.state.dvirData?.payload?.vehicleDefects}
                    childRef={(ref) => (this.vehicleDefectsTableRef = ref())}
                    fetchError={this.state.fetchError}
                    loading={this.state.loading}
                    onDefectResolveClick={this.handleDefectResolveClick}
                    onDefectMechanicNotesChange={
                      this.handleDefectMechanicNotesChange
                    }
                    onSaveMechanicNotes={this.handleMechanicNotesChangeSave}
                    onConfirmResolveDefect={this.handleDefectResolveConfirm}
                    onMarkAllAsResolvedClick={this.handleMarkAllAsResolvedClick}
                    onCreateWorkOrderFromDefectsClick={
                      this.handleCreateWorkOrderClick
                    }
                    onCreateTMTWorkOrderFromDefectsClick={
                      this.handleCreateTMTWorkOrderClick
                    }
                    createWorkOrderFromDefectsEnabled={
                      customFleets.summitMaterialsAll.includes(
                        ShopStore.currentShop.id
                      ) === false
                    }
                    isGeotabFleet={this.isGeotabFleet()}
                  />
                </Col>
              </VehicleDefectsRow>
            </>
          )}

          {this.state.dvirData?.payload?.trailerDefects?.length > 0 && (
            <>
              {/* <Divider />
            <VehicleDefectsRow gutter={16}>
              <Col span={24}>
                <Title level={3}>Trailer Defects</Title>
              </Col>
              {this.state.dvirData?.payload?.trailerDefects.map(
                (defect, index) => {
                  return (
                    <Col key={defect.id} span={6}>
                      <Card
                        className="card"
                        style={{ width: '100%' }}
                        cover={
                          <img
                            height={250}
                            className="image"
                            alt="example"
                            src="https://media.istockphoto.com/id/1409329028/vector/no-picture-available-placeholder-thumbnail-icon-illustration-design.jpg?s=612x612&w=0&k=20&c=_zOuJu755g2eEUioiOUdz_mHKJQJn-tDgIAhQzyeKUQ="
                          />
                        }
                      >
                        <Title style={{ marginBottom: 0 }} level={3}>
                          {defect.defectType}
                        </Title>
                        <Text type="secondary">
                          Added on {moment(defect.createdAtTime).format('lll')}
                        </Text>
                        <Divider />
                        <Text strong>Driver Notes</Text>
                        <FakeTextArea>
                          <Text>{defect.comment}</Text>
                        </FakeTextArea>
                        <div style={{ width: '100%' }}>
                          <CustomTag>
                            <Text
                              className={defect.isResolved ? 'green' : 'red'}
                              style={{ color: 'white' }}
                              strong
                            >
                              {defect.isResolved ? 'RESOLVED' : 'UNRESOLVED'}
                            </Text>
                          </CustomTag>
                        </div>
                      </Card>
                    </Col>
                  );
                }
              )}
            </VehicleDefectsRow> */}
              <Divider />
              <VehicleDefectsRow gutter={16}>
                <Col span={24}>
                  <Title level={3}>Trailer Defects</Title>
                </Col>
                <Col span={24}>
                  <VehicleDefectsTable
                    createTmtWorkOrdersEnabled={false}
                    data={this.state.dvirData?.payload?.trailerDefects}
                    childRef={(ref) => (this.trailerDefectsTableRef = ref())}
                    fetchError={this.state.fetchError}
                    loading={this.state.loading}
                    onDefectResolveClick={(key, defect) => {
                      this.handleDefectResolveClick(key, defect, 'trailers');
                    }}
                    onDefectMechanicNotesChange={(defect, value) => {
                      this.handleDefectMechanicNotesChange(
                        defect,
                        value,
                        'trailers'
                      );
                    }}
                    onSaveMechanicNotes={(defect, mechanicNotes) => {
                      this.handleMechanicNotesChangeSave(
                        defect,
                        mechanicNotes,
                        'trailers'
                      );
                    }}
                    onConfirmResolveDefect={(defect) => {
                      this.handleDefectResolveConfirm(defect, 'trailers');
                    }}
                    onMarkAllAsResolvedClick={this.handleMarkAllAsResolvedClick}
                    onCreateWorkOrderFromDefectsClick={
                      this.handleCreateWorkOrderClick
                    }
                    isGeotabFleet={this.isGeotabFleet()}
                  />
                </Col>
              </VehicleDefectsRow>
            </>
          )}

          <Divider />
          <ConfirmationRow gutter={16}>
            <Col span={24}>
              <Title style={{ marginBottom: 0 }} level={3}>
                Confirmation
              </Title>
              <Text type="secondary">Driver Signature</Text>
            </Col>
            <Col style={{ marginTop: '1rem' }} span={18}>
              <Text>
                "I certify that this vehicle has been inspected in accordance
                with the applicable requirements"
              </Text>
              <br />
              <Text type="secondary">
                {moment(
                  this.state.dvirData?.payload?.authorSignature?.signedAtTime
                ).format('lll')}
              </Text>
            </Col>
            <Col span={6}>
              <SignatureRow>
                <div className="driver-name">
                  <Text strong>
                    {
                      this.state.dvirData?.payload?.authorSignature
                        ?.signatoryUser?.name
                    }
                  </Text>
                </div>
                <div className="driver-signature">
                  <Text type="secondary">Driver Signature</Text>
                </div>
              </SignatureRow>
            </Col>
          </ConfirmationRow>

          {this.state.dvirData?.payload?.secondSignature?.signatoryUser
            ?.name && (
            <>
              <Divider />
              <ConfirmationRow gutter={16}>
                <Col span={24}>
                  <Title style={{ marginBottom: 0 }} level={3}>
                    &nbsp;
                  </Title>
                  <Text type="secondary">Mechanic Signature</Text>
                </Col>
                <Col style={{ marginTop: '1rem' }} span={18}>
                  <Text>
                    "This unsafe DVIR has been resolved and the vehicle is now
                    safe to drive."
                  </Text>
                  <br />
                  <Text type="secondary">
                    {moment(
                      this.state.dvirData?.payload?.secondSignature
                        ?.signedAtTime
                    ).format('lll')}
                  </Text>
                </Col>
                <Col span={6}>
                  <SignatureRow>
                    <div className="driver-name">
                      <Text strong>
                        {
                          this.state.dvirData?.payload?.secondSignature
                            ?.signatoryUser?.name
                        }
                      </Text>
                    </div>
                    <div className="driver-signature">
                      <Text type="secondary">Mechanic Signature</Text>
                    </div>
                  </SignatureRow>
                </Col>
              </ConfirmationRow>
            </>
          )}

          {/* Handle DVIR resolve modal */}
          <DarkHeaderModal
            title="Resolve DVIR"
            visible={this.state.resolveDefectModalVisible}
            onCancel={this.handleResolveDVIRCancel}
            footer={[
              <Button key="back" onClick={this.handleResolveDVIRCancel}>
                Cancel
              </Button>,
              <Button
                key="submit"
                type="primary"
                onClick={this.handleResolveDVIR}
                loading={this.state.isResolvingDVIR}
              >
                Resolve
              </Button>,
            ]}
          >
            <Text strong>Resolved By:</Text>
            <Input
              value={CurrentUserStore.user.email}
              disabled={true}
              style={{
                width: '100%',
                display: 'block',
                marginBottom: '1rem',
              }}
            />
            <Text style={{ marginTop: '1rem' }} strong>
              {this.isGeotabFleet() ? 'Remark:' : 'Mechanic Notes:'}
            </Text>
            <TextArea
              rows={4}
              value={this.state.mechanicNotes}
              onChange={(e) => this.setState({ mechanicNotes: e.target.value })}
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginTop: '1rem',
              }}
            >
              <Input
                type="checkbox"
                style={{
                  marginRight: '1rem',
                  flex: 1,
                  height: 15,
                }}
                id="resolveAllDefectsCheckbox"
                onChange={() => {
                  if (this.state.resolveAllDefectsError) {
                    this.setState({ resolveAllDefectsError: false });
                  }
                }}
              />
              <label
                htmlFor="resolveAllDefectsCheckbox"
                style={{ flex: 11, cursor: 'pointer' }}
              >
                <Text type="secondary">
                  I want to resolve all{' '}
                  <b>
                    {get(
                      this,
                      'state.dvirData.payload.vehicleDefects',
                      []
                    ).filter((d) => !d.resolvedBy).length ||
                      get(
                        this,
                        'state.dvirData.payload.trailerDefects',
                        []
                      ).filter((d) => !d.resolvedBy).length}
                  </b>{' '}
                  defects in this DVIR as well and apply the mechanic note
                  written above to each defect
                  {this.isGeotabFleet() && (
                    <span style={{ color: 'red', fontWeight: 'bold' }}>
                      {' '}
                      - This is required to make the DVIR safe.
                    </span>
                  )}
                </Text>
              </label>
            </div>
            {this.state.resolveAllDefectsError && (
              <div style={{ marginTop: '0.5rem' }}>
                <Text style={{ color: 'red', fontWeight: 'bold' }}>
                  It is required to resolve all defects in order to make this
                  DVIR safe.
                </Text>
              </div>
            )}
          </DarkHeaderModal>
        </PageWrapper>

        {/* Confirm resolve defects */}
        <DarkHeaderModal
          title="Resolve Defect(s)"
          visible={this.state.resolveSelectedDefectsModalVisible}
          onCancel={this.handleResolveSelectedDefectsCancel}
          footer={[
            <Button
              key="back"
              onClick={this.handleResolveSelectedDefectsCancel}
            >
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={this.handleResolveSelectedDefects}
              loading={this.state.isResolvingDefects}
            >
              Resolve
            </Button>,
          ]}
        >
          <Text strong>Resolved By:</Text>
          <Input
            value={CurrentUserStore.user.email}
            disabled={true}
            style={{
              width: '100%',
              display: 'block',
              marginBottom: '1rem',
            }}
          />
          <Text style={{ marginTop: '1rem' }} strong>
            {this.isGeotabFleet() ? 'Remark:' : 'Mechanic Notes:'}
            <i>
              (If resolving multiple defects at once, notes entered below will
              be added to all defects)
            </i>
            :
          </Text>
          <TextArea
            rows={4}
            onChange={(e) =>
              this.setState({
                mechanicNotesResolvingMultipleDefects: e.target.value,
              })
            }
          />
        </DarkHeaderModal>

        {/* Confirm Create Work Order from defects */}
        <CreateWorkOrderModal
          key={1}
          modalId="CREATE_WORK_ORDER_MODAL_ID"
          issues={this.state.defectsToCreateWorkOrder}
          title={'Create Work Order'}
          isLoading={this.state.isCreatingWorkOrder}
          loading={false}
          onSave={this.handleCreateWorkOrder}
          type="defects"
        />

        {/* Confirm Create TMT Work Order from defects */}
        <CreateTMTWorkOrderModal
          key={2}
          modalId="CREATE_TMT_WORK_ORDER_MODAL_ID"
          issues={this.state.defectsToCreateWorkOrder}
          title={'Create Work Order'}
          isLoading={this.state.isCreatingTMTWorkOrder}
          loading={false}
          onSave={this.handleCreateTMTWorkOrder}
          tmtRepairShop={this.state.tmtRepairShop}
        />
      </Fragment>
    );
  }
}

export default observer(DVIRDetailsPage);
